import { Tooltip } from "antd";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { t } from "ttag";

import Button from "metabase/common/components/Button/Button";
import { trackEvent } from "metabase/jerry/Common/jerry-utils";

import { DataCopilotIcon } from "./DataCopilotIcon";

function extractBaseUrl(data: unknown) {
  if (!data || typeof data !== "object") {
    return undefined;
  }

  const payload = data as {
    base_url?: unknown;
    data?: { base_url?: unknown };
  };

  if (typeof payload.base_url === "string") {
    return payload.base_url;
  }

  if (payload.data && typeof payload.data.base_url === "string") {
    return payload.data.base_url;
  }

  return undefined;
}

const LOCAL_DATA_COPILOT_URL = "http://127.0.0.1:8000";
const PROD_DATA_COPILOT_URL = "https://data-copilot.ing.getjerry.com";

let dataCopilotBaseUrlPromise: Promise<string> | null = null;

function getDefaultDataCopilotBaseUrl() {
  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    if (hostname === "127.0.0.1" || hostname === "localhost") {
      return LOCAL_DATA_COPILOT_URL;
    }
  }

  return PROD_DATA_COPILOT_URL;
}

async function getDataCopilotBaseUrl() {
  if (!dataCopilotBaseUrlPromise) {
    dataCopilotBaseUrlPromise = fetch("/api/jerry/data-copilot/config")
      .then(async (response) => {
        const data = (await response.json().catch(() => ({}))) as unknown;
        const baseUrl = extractBaseUrl(data) ?? getDefaultDataCopilotBaseUrl();
        if (!response.ok || !baseUrl) {
          throw new Error(
            `Failed to load Data Copilot config (${response.status})`,
          );
        }
        return baseUrl;
      })
      .catch((error) => {
        dataCopilotBaseUrlPromise = null;
        throw error;
      });
  }

  return dataCopilotBaseUrlPromise;
}

export const DataCopilotNavButton = () => {
  const [dataCopilotBaseUrl, setDataCopilotBaseUrl] = useState(
    getDefaultDataCopilotBaseUrl(),
  );

  useEffect(() => {
    let isCancelled = false;

    void getDataCopilotBaseUrl()
      .then((baseUrl) => {
        if (!isCancelled) {
          setDataCopilotBaseUrl(baseUrl);
        }
      })
      .catch(() => undefined);

    return () => {
      isCancelled = true;
    };
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      window.open(dataCopilotBaseUrl, "_blank", "noopener,noreferrer");

      trackEvent(
        {
          eventLabel: "Open Data Copilot Nav Button",
        },
        {
          href: dataCopilotBaseUrl,
        },
      );
    },
    [dataCopilotBaseUrl],
  );

  return (
    <Tooltip title={t`Go to Data Copilot`}>
      <Button
        onClick={handleClick}
        className="data_copilot_button"
        icon={
          <DataCopilotIcon
            size={23}
            style={{
              display: "block",
              transform: "translateY(1px)",
            }}
          />
        }
        onlyIcon
        data-testid="data-copilot-link"
        style={{
          padding: "-4px",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginRight: "-3px",
        }}
      />
    </Tooltip>
  );
};
